import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../services/api';

const EODPage = () => {
  const [systemDay, setSystemDay] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [eodResult, setEodResult] = useState(null);

  useEffect(() => {
    fetchSystemDay();
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setIsAdmin(payload && payload.role === 'admin');
      } catch {}
    }
  }, []);

  const fetchSystemDay = async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_BASE_URL}/system-day`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSystemDay(res.data.system_day);
    } catch (err) {
      setError('Failed to fetch system day');
    }
    setLoading(false);
  };

  const handleRunEOD = () => {
    setError('');
    setMessage('');
    setEodResult(null);
    setShowConfirm(true);
  };

  const confirmAndRunEOD = async () => {
    setShowConfirm(false);
    setLoading(true);
    setError('');
    setMessage('');
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API_BASE_URL}/money-market/eod`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEodResult({
        previousDay: systemDay,
        nextDay: res.data.next_system_day,
        details: res.data.message
      });
      setSystemDay(res.data.next_system_day);
      setMessage(`EOD completed successfully. System day advanced to ${res.data.next_system_day}`);
    } catch (err) {
      setError(err.response?.data?.message || 'EOD failed');
    }
    setLoading(false);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
      });
    } catch { return dateStr; }
  };

  const computeNextDay = () => {
    if (!systemDay) return '';
    try {
      const d = new Date(systemDay);
      d.setDate(d.getDate() + 1);
      return d.toISOString().slice(0, 10);
    } catch { return ''; }
  };

  return (
    <div className="eod-page ui-page">
      <div className="ui-card eod-card">
        <h2>End of Day (EOD) Operations</h2>
        <div className="eod-current-day" style={{ fontSize: '1.1rem', margin: '16px 0' }}>
          <strong>Current System Day:</strong>{' '}
          <span style={{ fontSize: '1.2rem', fontWeight: 600 }}>
            {systemDay ? formatDate(systemDay) : 'Loading...'}
          </span>
          {systemDay && (
            <span style={{ marginLeft: 8, color: '#666', fontSize: '0.9rem' }}>({systemDay})</span>
          )}
        </div>

        <button
          className="eod-btn ui-btn ui-btn-primary"
          onClick={handleRunEOD}
          disabled={loading || !isAdmin}
          style={{ minWidth: 180, padding: '10px 24px', fontSize: '1rem' }}
        >
          {loading ? 'Running EOD...' : 'Run EOD'}
        </button>

        {/* Success result card */}
        {eodResult && (
          <div style={{
            marginTop: 20, padding: 20, background: '#d4edda', border: '1px solid #c3e6cb',
            borderRadius: 8, color: '#155724'
          }}>
            <h4 style={{ margin: '0 0 12px 0' }}>EOD Completed Successfully</h4>
            <div style={{ marginBottom: 8 }}>
              <strong>Previous System Day:</strong> {formatDate(eodResult.previousDay)} ({eodResult.previousDay})
            </div>
            <div style={{ marginBottom: 8, fontSize: '1.1rem' }}>
              <strong>New System Day:</strong>{' '}
              <span style={{ fontWeight: 700 }}>{formatDate(eodResult.nextDay)} ({eodResult.nextDay})</span>
            </div>
            <hr style={{ borderColor: '#c3e6cb' }} />
            <div style={{ fontSize: '0.9rem' }}>
              <strong>Details:</strong> {eodResult.details}
            </div>
          </div>
        )}

        {message && !eodResult && <div className="eod-success ui-alert-success">{message}</div>}
        {error && <div className="eod-error ui-alert-error" style={{ marginTop: 16 }}>{error}</div>}
        {!isAdmin && (
          <div className="eod-warning">You do not have permission to run EOD.</div>
        )}
      </div>

      {/* Confirmation Modal */}
      {showConfirm && (
        <div style={{
          position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
          background: 'rgba(0,0,0,0.4)', zIndex: 2000, display: 'flex',
          alignItems: 'center', justifyContent: 'center'
        }}>
          <div style={{
            background: '#fff', borderRadius: 10, padding: 32, minWidth: 420,
            maxWidth: 520, boxShadow: '0 8px 32px rgba(0,0,0,0.2)'
          }}>
            <h4 style={{ margin: '0 0 16px 0' }}>Confirm EOD Process</h4>
            <div style={{ marginBottom: 12 }}>
              Are you sure you want to run the End of Day process?
            </div>
            <div style={{
              background: '#f8f9fa', borderRadius: 6, padding: 12, marginBottom: 16
            }}>
              <div style={{ marginBottom: 6 }}>
                <strong>Current System Day:</strong> {formatDate(systemDay)} ({systemDay})
              </div>
              <div>
                <strong>Will advance to:</strong>{' '}
                <span style={{ fontWeight: 700, color: '#0d6efd' }}>
                  {formatDate(computeNextDay())} ({computeNextDay()})
                </span>
              </div>
            </div>
            <div style={{ color: '#856404', background: '#fff3cd', border: '1px solid #ffeeba', borderRadius: 6, padding: 10, marginBottom: 20, fontSize: '0.9rem' }}>
              This will process all accruals, maturity entries, and ledger postings for {formatDate(systemDay)}, then advance the system day. This action cannot be undone.
            </div>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button
                className="ui-btn"
                onClick={() => setShowConfirm(false)}
                style={{ padding: '8px 20px', background: '#6c757d', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                className="ui-btn ui-btn-primary"
                onClick={confirmAndRunEOD}
                style={{ padding: '8px 20px', background: '#dc3545', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}
              >
                Confirm & Run EOD
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EODPage;
